import './tw-overrides.css';
import arrow from '../../../images/down_arrow.svg';
import ArrowIcon from '../../../images/down_arrow.svg?react';
import { Link } from 'react-router-dom';
import {
  DropDownList as KendoDropDownList,
  DropDownListProps as KendoDropDownListProps,
} from '@progress/kendo-react-dropdowns';

const cx = (...parts: (string | undefined | false)[]) =>
  parts.filter(Boolean).join(' ');

export type Props = {
  children: React.ReactNode;
};

export type IdProps = {
  children: React.ReactNode;
  id?: string;
};

export type ClassNameProps = {
  children: React.ReactNode;
  className?: string;
};

export type GridContainerProps = {
  children: React.ReactNode;
  id?: string;
};

export type ColumnContainerProps = {
  children: React.ReactNode;
  id?: string;
  cols?: number;
};

export const GridContainer = ({ children, id }: GridContainerProps) => {
  return (
    <div id={id} className="tw-grid tw-grid-cols-1 md:tw-grid-cols-12 tw-gap-4">
      {children}
    </div>
  );
};

export const ColumnContainer = ({
  children,
  id = 'column-container',
  cols = 9,
}: ColumnContainerProps) => {
  return (
    <div id={id} className={`tw-col-span-12 lg:tw-col-span-${cols} tw-px-0`}>
      {children}
    </div>
  );
};

export const ContentContainer = ({ children }: Props) => {
  return (
    <div className="tw-bg-white tw-rounded-lg tw-shadow-md">{children}</div>
  );
};

export const ListViewBody = ({ children }: Props) => {
  return (
    <div className="tw-bg-white tw-p-4 tw-pt-0 md:tw-px-8 md:tw-py-6">
      {children}
    </div>
  );
};

/////////////////////////// - Responsive Views - ///////////////////////////////

export const MobileContainerSM = ({ children, className }: ClassNameProps) => {
  return (
    <div className={`tw-flex sm:tw-hidden tw-w-full ${className}`}>
      {children}
    </div>
  );
};

export const DesktopContainerSM = ({ children, className }: ClassNameProps) => {
  return (
    <div className={`tw-hidden sm:tw-flex tw-w-full ${className}`}>
      {children}
    </div>
  );
};

/////////////////////////// - Text components - ///////////////////////////////

export const TitleBar = ({ children }: Props) => {
  return (
    <div className="tw-flex tw-flex-col tw-items-start tw-border-0 tw-border-solid tw-border-transparent tw-border-unm-border1 tw-px-4 tw-py-3 tw-min-h-[83px] md:tw-flex-row md:tw-justify-between md:tw-items-center md:tw-border-b md:tw-px-8">
      {children}
    </div>
  );
};

export const Title = ({ children }: Props) => {
  return (
    <div className="tw-text-unm-adhoc2 tw-font-ubuntu tw-font-light tw-text-2xl tw-pb-5 tw-mr-4 md:tw-pb-0">
      {children}
    </div>
  );
};

export const ItalicText = ({ children }: Props) => {
  return <p className="tw-text-unm-adhoc2 tw-italic mt-4">{children}</p>;
};

interface TextProps {
  id?: string;
  fontSize?: string;
  fontWeight?: string;
  color?: string;
  align?: string;
  className?: string;
  children: React.ReactNode;
}

export const Text = ({
  id,
  fontSize = 'sm',
  fontWeight = 'normal',
  color = 'unm-primary01-100',
  align = 'left',
  className = '',
  children,
}: TextProps) => {
  return (
    <div
      id={id}
      className={`tw-font-sans tw-font-${fontWeight} tw-text-sm md:tw-text-${fontSize} tw-text-${color} tw-text-${align} ${className}`}
    >
      {children}
    </div>
  );
};

export const TextBase = (props: TextProps) => {
  return (
    <Text {...props} fontSize="base">
      {props.children}
    </Text>
  );
};

export const HeaderTextSmall = (props: TextProps) => {
  return (
    <Text {...props} fontWeight="semibold">
      {props.children}
    </Text>
  );
};

export const HeaderTextMedium = (props: TextProps) => {
  return (
    <Text {...props} fontSize="base" fontWeight="semibold">
      {props.children}
    </Text>
  );
};

export const HighlightNote = (props: TextProps) => {
  return (
    <div
      className={`tw-py-1 tw-px-3 tw-rounded tw-border-unm-adhoc2 tw-border tw-border-solid tw-w-full ${props.className}`}
    >
      <Text {...props} color="unm-adhoc2">
        {props.children}
      </Text>
    </div>
  );
};

export const SingleBorderContainer = ({
  children,
  className,
}: ClassNameProps) => {
  return (
    <div
      className={`tw-p-3 tw-rounded tw-border-unm-adhoc2 tw-border tw-border-solid tw-w-full ${className}`}
    >
      {children}
    </div>
  );
};

export const ShadowBorderContainer = ({
  children,
  className,
}: ClassNameProps) => {
  return (
    <div
      className={`tw-p-5 tw-bg-white tw-rounded tw-shadow-custom ${className}`}
    >
      {children}
    </div>
  );
};

export const baseShadowSmall = 'tw-shadow-[0px_2px_4px_0px_rgba(2,84,126,0.2)]';
export const baseShadowMedium =
  'tw-shadow-[0px_3px_6px_0px_rgba(2,84,126,0.2)]';
//////////////////////////////////////////////////////////////////

interface DocumentLinkProps {
  id?: string;
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}

interface IconButtonProps extends DocumentLinkProps {
  to?: string;
}

export const DocumentLink = ({
  children,
  id,
  onClick,
  disabled,
}: DocumentLinkProps) => {
  return (
    <button
      id={id}
      onClick={onClick}
      disabled={disabled}
      className="tw-bg-white tw-border-0 tw-text-unm-interactive01-400 tw-font-sans tw-text-base tw-font-normal tw-p-0 tw-align-top tw-flex tw-gap-1 tw-items-center"
    >
      {children}
    </button>
  );
};

export const IconButton = ({
  children,
  id,
  onClick,
  disabled,
  to,
}: IconButtonProps) => {
  const className = `
    tw-bg-${disabled ? '[#FAFAFA]' : 'white'}
    tw-border tw-border-unm-interactive01-400
    tw-rounded-md
    tw-text-${disabled ? '[#A8A8A8]' : 'unm-interactive01-400'}
    tw-font-sans tw-text-base tw-font-semibold
    tw-h-[30px] tw-leading-[14px] tw-m-0
     sm:tw-max-w-[280px]
    tw-w-full
  `;

  if (to && !disabled) {
    return (
      <Link to={to} onClick={onClick} id={id} className={className}>
        <button id={id} disabled={disabled} className={className}>
          {children}
        </button>
      </Link>
    );
  }

  return (
    <button id={id} onClick={onClick} disabled={disabled} className={className}>
      {children}
    </button>
  );
};

interface LinkProps {
  linkColor?: string;
  fontSize?: string;
  to: string;
  children: React.ReactNode;
}

export const StyledLink = ({
  children,
  to,
  linkColor = 'unm-interactive01-400',
  fontSize = 'sm',
}: LinkProps) => {
  return (
    <Link
      to={to}
      className={` tw-text-${fontSize} tw-text-${linkColor} md:tw-text-base tw-font-semibold  tw-no-underline`}
    >
      {children}
    </Link>
  );
};

export const NoDocumentsContainer = ({ children }: Props) => {
  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-mx-auto tw-max-w-[405px] tw-py-24 tw-pb-52 tw-text-center">
      {children}
    </div>
  );
};

export const LoadingCover = ({ children }: Props) => {
  return (
    <div className="tw-absolute tw-flex tw-justify-center tw-items-start md:tw-items-center tw-w-full tw-h-full tw-top-4 tw-left-0 tw-bg-white tw-z-[10004] tw-opacity-95">
      {children}
    </div>
  );
};

export const LoadingCoverSmall = ({ children, id }: IdProps) => {
  return (
    <div
      {...(id ? { id } : {})}
      className="tw-bg-white tw-z-[10004] tw-opacity-95 tw-flex tw-justify-center tw-items-center tw-py-6"
    >
      {children}
    </div>
  );
};

///////////////////////////////////// Card  ////////////////////////////

export const Card = ({ children }: Props) => {
  return (
    <div className="tw-my-2 tw-bg-white tw-rounded tw-border-unm-interactive01-400 tw-border tw-border-solid tw-overflow-hidden tw-w-full">
      {children}
    </div>
  );
};

///////////////////////////////////// Expandable Card ///////////////////////////

interface ExpandableCardProps {
  id?: string;
  onClick: () => void;
  children: React.ReactNode;
}

export const ExpandableCard = ({ children }: Props) => {
  return (
    <div className="tw-expandable-card tw-my-2 tw-bg-white tw-rounded tw-border-unm-interactive01-400 tw-border tw-border-solid tw-overflow-hidden tw-w-full">
      {children}
    </div>
  );
};

export const ExpandableCardHeader = ({
  id,
  onClick,
  children,
}: ExpandableCardProps) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div // NOSONAR
      id={id}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className="tw-flex tw-flex-row tw-justify-between tw-items-center tw-p-3 tw-bg-white tw-cursor-pointer tw-relative tw-w-full tw-z-10"
    >
      {children}
    </div>
  );
};

export const ExpandedContent = ({ children }: Props) => {
  return (
    <div className="tw-pt-3 tw-bg-white tw-overflow-hidden tw-w-full">
      {children}
    </div>
  );
};

interface ArrowProps {
  rotate: string;
}

export const Arrow = ({ rotate }: ArrowProps) => {
  return (
    <img
      alt="Expand"
      src={arrow}
      className={`tw-cursor-pointer tw-w-11 tw-transition-all tw-duration-500 tw-select-none tw-text-unm-interactive01-400 ${
        rotate === 'true'
          ? 'tw-transform tw-rotate-180'
          : 'tw-transform tw-rotate-0'
      }`}
    />
  );
};

export const ArrowWithDescription = ({ rotate }: ArrowProps) => {
  return (
    <div className="tw-flex tw-items-center tw-justify-end">
      <div className="tw-mr-2 tw-text-sm  tw-font-sans tw-font-semibold tw-text-unm-interactive01-400">
        {rotate === 'true' ? 'Close details' : 'Open details'}
      </div>
      <ArrowIcon
        className={`tw-cursor-pointer tw-w-11 tw-transition-all tw-duration-500 tw-select-none tw-text-unm-interactive01-400 ${
          rotate === 'true'
            ? 'tw-transform tw-rotate-180'
            : 'tw-transform tw-rotate-0'
        }`}
      ></ArrowIcon>
      {/* <img
        alt="Expand"
        src={arrow}
        className={`tw-cursor-pointer tw-w-11 tw-transition-all tw-duration-500 tw-select-none tw-text-unm-interactive01-400 ${
          rotate === 'true'
            ? 'tw-transform tw-rotate-180'
            : 'tw-transform tw-rotate-0'
        }`}
      /> */}
    </div>
  );
};

//////////////////////////////////Flags //////////////////////////////

interface StatusProps {
  children: React.ReactNode;
  background: string;
  color: string;
}

export const StatusIndicator = ({
  children,
  background,
  color,
}: StatusProps) => {
  return (
    <div
      className={`tw-rounded-md tw-text-center tw-text-${color} tw-text-sm md:tw-text-base tw-font-semibold tw-uppercase tw-bg-${background} tw-px-3 tw-py-1 tw-w-38`}
    >
      {children}
    </div>
  );
};

////////////////////////////////////File Selection: NotificationBannerText////////////////////////////

export const NotificationBannerText = ({
  children,
  id,
  textColorClass,
}: IdProps & { textColorClass?: string }) => {
  return (
    <div
      id={id}
      className={`tw-text-sm tw-pl-4 tw-font-sans tw-font-semibold tw-whitespace-normal ${
        textColorClass ?? 'tw-text-color-unm-adhoc2'
      }`}
    >
      {children}
    </div>
  );
};

export const NotificationBanner = ({ children, id }: IdProps) => {
  return (
    <div
      id={id}
      className="tw-flex tw-items-center tw-bg-unm-pending-bg tw-text-base tw-font-sans tw-rounded-lg tw-my-1 tw-mx-0 tw-px-3 tw-py-4"
    >
      {/* <img alt="info" src={orangeWarning} className="tw-self-center" /> */}
      {children}
    </div>
  );
};

/////////////////////////////////////Next Tasks///////////////////////////////////////

type DividerProps = {
  id?: string;
  className?: string;
};

type GoToTaskButtonProps = {
  to: {
    pathname: string;
    state?: any;
  };
  children: React.ReactNode;
  className?: string;
};

export const NextTaskContainer = ({ children }: Props) => {
  return (
    <div className="tw-bg-white tw-shadow-md tw-rounded-lg tw-p-8 tw-mb-2">
      {children}
    </div>
  );
};

export const NextTaskTitle = ({ children }: Props) => {
  return (
    <div className="tw-text-unm-interactive01-800 tw-font-ubuntu tw-text-2xl tw-font-normal tw-mb-2 tw-flex">
      {children}
    </div>
  );
};

export const NextTaskDivider = ({ id, className }: DividerProps) => {
  return (
    <div
      id={id}
      className={`tw-h-px tw-bg-unm-secondary01-700 tw-mb-6 ${className}`}
    />
  );
};

export const NextTaskCard = ({ children }: Props) => (
  <div
    className="
      tw-flex tw-flex-nowrap tw-items-stretch
      tw-justify-between tw-gap-4 tw-mb-4
    "
  >
    {children}
  </div>
);

export const NextTaskCardDetails = ({ children }: Props) => (
  <div
    className="
      tw-flex tw-items-center tw-justify-between
      tw-flex-grow
      tw-rounded-xl tw-px-2 tw-py-0 tw-bg-white
      tw-transition-all sm:tw-flex-row tw-flex-col
      tw-border tw-border-solid tw-border-unm-interactive01-800 
    "
  >
    {children}
  </div>
);

export const NextTaskCardTitle = ({ children }: Props) => (
  <div
    className="
      tw-flex-1 tw-text-left tw-font-semibold
      tw-text-unm-primary01-100
      tw-break-words
      sm:tw-max-w-[35%]
    "
  >
    {children}
  </div>
);

export const NextTaskCardDueDate = ({ children }: Props) => (
  <div
    className="
      tw-flex-1 tw-text-center
      tw-text-xs tw-text-unm-primary01-100
      sm:tw-w-[20%] tw-mt-0 sm:tw-mt-0
    "
  >
    {children}
  </div>
);

export const NextTaskCardCategory = ({ children }: Props) => (
  <div
    className="
      tw-flex-1 tw-text-center
      tw-text-xs tw-uppercase tw-text-unm-primaryblue tw-underline
      sm:tw-w-[20%] tw-mt-0 sm:tw-mt-0
    "
  >
    {children}
  </div>
);

export const NextTaskCardStatus = ({
  text,
  background,
  color,
}: {
  text: string;
  background: string;
  color: string;
}) => (
  <div className="tw-flex tw-justify-end tw-flex-1 sm:tw-w-[20%]">
    <span
      className="
        tw-text-xs tw-font-semibold tw-rounded-full
        tw-px-3 tw-py-1 tw-whitespace-nowrap tw-text-center
      "
      style={{ backgroundColor: background, color }}
    >
      {text}
    </span>
  </div>
);

export const GoToTaskButton = ({
  to,
  children,
  className,
}: GoToTaskButtonProps) => {
  return (
    <div
      className={`
        tw-flex tw-items-center
        tw-justify-center
        tw-w-auto
        tw-shrink-0
      `}
    >
      <Link
        to={to}
        className={`
          tw-bg-unm-primary02-300 tw-text-unm-primary02-100 tw-text-sm
          tw-font-semibold tw-rounded-full
          tw-px-5 tw-py-2.5
          tw-whitespace-nowrap tw-text-center
          tw-shadow-md
          ${className}`}
      >
        {children}
      </Link>
    </div>
  );
};

export const NextTaskOr = ({ children }: Props) => (
  <div className="tw-text-unm-interactive01-400 tw-font-semibold tw-text-base tw-mb-2">
    {children}
  </div>
);

export const GoToSummaryLink = ({ children }: Props) => (
  <div>
    <Link
      to="/app"
      className="tw-text-unm-interactive01-400 tw-font-semibold tw-underline tw-text-sm"
    >
      {children}
    </Link>
  </div>
);

//////////////////////////////////// Kendo components /////////////////////

export type DropDownListTwProps<TItem> = Omit<
  KendoDropDownListProps,
  'data' | 'value' | 'defaultValue' | 'onChange'
> & {
  data?: TItem[];
  value?: TItem | null;
  defaultValue?: TItem | null;
  onChange?: (args: { value: TItem | null }) => void;
  id?: string;
  maxwidth?: string;
  className?: string;
  inputTextColorClassName?: string;
};

export const DropDownListTw = <TItem,>({
  id,
  maxwidth,
  className,
  inputTextColorClassName = 'tw-text-unm-secondary01-700',
  data,
  value,
  defaultValue,
  onChange,
  ...rest
}: DropDownListTwProps<TItem>) => {
  return (
    <div
      {...(id ? { id } : {})}
      style={maxwidth ? { maxWidth: maxwidth } : undefined}
      className={cx(
        'tw-w-full',
        '[&_.k-dropdown-wrap]:tw-rounded-md',
        '[&_.k-dropdown-wrap]:tw-bg-white',
        '[&_.k-dropdown-wrap]:tw-border-unm-border1',
        '[&_.k-dropdown-wrap]:tw-h-8',
        '[&_.k-dropdown-wrap]:tw-flex',
        '[&_.k-dropdown-wrap]:tw-items-center',
        '[&_.k-picker-wrap]:tw-rounded-md',
        '[&_.k-picker-wrap]:tw-bg-white',
        '[&_.k-picker-wrap]:tw-border-unm-border1',
        '[&_.k-picker-wrap]:tw-h-8',
        '[&_.k-picker-wrap]:tw-flex',
        '[&_.k-picker-wrap]:tw-items-center',
        '[&_.k-input]:tw-font-sans',
        '[&_.k-input]:tw-font-normal',
        `[&_.k-input]:${inputTextColorClassName}`,
        '[&_option]:tw-font-sans',
        '[&_option]:tw-font-normal',
        `[&_option]:${inputTextColorClassName}`,
        className,
      )}
    >
      <KendoDropDownList
        // Cast to the non-generic Kendo props shape
        {...(rest as KendoDropDownListProps)}
        data={data as any}
        value={(value ?? null) as any}
        defaultValue={(defaultValue ?? null) as any}
        onChange={(e) =>
          onChange?.({ value: (e.value ?? null) as TItem | null })
        }
        className="tw-w-full"
      />
    </div>
  );
};
